'use client';

import { FC } from 'react';
import { ProjectSelectionDialog } from './ProjectSelectionDialog';
import { useDialogStore } from '@/stores/dialogStore';
import { ProjectRowType } from '@/components/tables/ProjectGridCore';
import { ProjectDto, ProjectTypeDto, ProjectStatusDto } from '@/api/ApiClient';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';

const OPTION_KEYS: OptionKey[] = ['projectType'];

/**
 * Wrapper component that connects ProjectSelectionDialog to the dialog store.
 * This is the global instance that should be rendered once in the app.
 */
export const ProjectSelectionDialogWrapper: FC = () => {
  const { projectSelectionDialog, closeProjectSelectionDialog } = useDialogStore();
  const { options } = useSelectOptions(OPTION_KEYS);

  const handleProjectsSelect = (projects: ProjectRowType[]) => {
    // Convert ProjectRowType[] to ProjectDto[]
    // Note: ProjectRowType.type is a string label, but ProjectDto.type needs ProjectTypeDto
    // We need to look up the ProjectTypeDto from options by matching the label
    const projectDtos: ProjectDto[] = projects.map((project) => {
      // Find the ProjectTypeDto that matches the type label
      const projectType: ProjectTypeDto | undefined = options.projectType?.find(
        (pt: ProjectTypeDto) => pt.label === project.type
      );

      return {
        id: project.id,
        projectLabel: project.projectLabel,
        type: projectType, // Now properly typed as ProjectTypeDto | undefined
        projectStatus: project.projectStatus as ProjectStatusDto,
        // Other fields will be fetched by the backend if needed
      };
    });

    closeProjectSelectionDialog(projectDtos);
  };

  const handleClose = () => {
    closeProjectSelectionDialog();
  };

  return (
    <ProjectSelectionDialog
      open={projectSelectionDialog.isOpen}
      title={projectSelectionDialog.title || 'Select Projects'}
      allowMultiple={projectSelectionDialog.allowMultiple !== false}
      onProjectsSelect={handleProjectsSelect}
      onProjectSelect={(project) => {
        // Single selection fallback
        handleProjectsSelect([project]);
      }}
      onClose={handleClose}
    />
  );
};
